import { fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import cookie from 'react-cookies'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import PointOfSaleView from './PointOfSaleView'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 25

const getNumberParam = (searchParams, key, fallback) => {
  const value = Number(searchParams.get(key))
  return Number.isFinite(value) && value > 0 ? value : fallback
}

const PointOfSaleApp = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [isFetching, setIsFetching] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const queryState = useMemo(
    () => ({
      q: searchParams.get('q') || '',
      page: getNumberParam(searchParams, 'page', DEFAULT_PAGE),
      perPage: getNumberParam(searchParams, 'per_page', DEFAULT_PER_PAGE)
    }),
    [searchParams]
  )

  const listPath = `${location.pathname}${location.search}`

  const title = queryState.q
    ? `Resultados de la búsqueda: ${queryState.q}`
    : 'Tareas en punto de venta'

  const updateQuery = useCallback(
    (changes, options = {}) => {
      const nextParams = new URLSearchParams(searchParams)

      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          nextParams.delete(key)
        } else {
          nextParams.set(key, String(value))
        }
      })

      if (options.resetPage) nextParams.set('page', String(DEFAULT_PAGE))
      if (!nextParams.get('per_page')) nextParams.set('per_page', String(queryState.perPage))

      setSearchParams(nextParams)
    },
    [queryState.perPage, searchParams, setSearchParams]
  )

  const fetchDataFromServer = useCallback(async () => {
    setIsFetching(true)

    const requestParams = new URLSearchParams({
      page: String(queryState.page),
      per_page: String(queryState.perPage)
    })

    if (queryState.q) requestParams.set('q', queryState.q)
    requestParams.set('business_tenant_uid', cookie.load('business_tenant_uid'))

    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/pos_tasks?${requestParams.toString()}`, {
        method: 'GET'
      })

      setData(response?.result || [])
      setTotalCount(response?.count || 0)
    } catch (error) {
      console.error('Error fetching data:', error)
      setData([])
      setTotalCount(0)
    } finally {
      setIsFetching(false)
    }
  }, [queryState.page, queryState.perPage, queryState.q])

  useEffect(() => {
    fetchDataFromServer()
  }, [fetchDataFromServer])

  const handleSearchUpdate = (newQuery) => {
    updateQuery({ q: newQuery.trim() }, { resetPage: true })
  }

  const handlePageChange = (_event, page) => {
    updateQuery({ page })
  }

  const handleRowsPerPageChange = (_event, rowsPerPage) => {
    updateQuery({ per_page: rowsPerPage, page: DEFAULT_PAGE })
  }

  const handleCreate = () => {
    navigate('/crear_tarea_punto_venta', { state: { from: listPath } })
  }

  const handleShow = (uuid) => {
    navigate(`/ejecucion_punto_venta/detalles/${uuid}`, { state: { from: listPath } })
  }

  const handleClone = (uuid) => {
    navigate(`/crear_tarea_punto_venta?clone=${uuid}`, { state: { from: listPath } })
  }

  const handleSuspend = async (uuid) => {
    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/pos_tasks/${uuid}`, {
        method: 'PUT',
        body: JSON.stringify({ suspended: true })
      })

      if (!response) return

      if (!response.suspended) {
        throw new Error('No se pudo suspender la tarea.')
      }

      await fetchDataFromServer()
    } catch (error) {
      if (error?.message === 'HTTP error 404') {
        throw new Error('No se encontró la tarea.')
      }

      console.error('Error suspending POS task:', error)
      throw new Error('No se pudo suspender la tarea. Intente nuevamente.')
    }
  }

  return (
    <PointOfSaleView
      data={data}
      isFetching={isFetching}
      title={title}
      query={queryState.q}
      onSearchUpdate={handleSearchUpdate}
      onCreate={handleCreate}
      onShow={handleShow}
      onClone={handleClone}
      onSuspend={handleSuspend}
      page={queryState.page}
      rowsPerPage={queryState.perPage}
      totalCount={totalCount}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  )
}

export default PointOfSaleApp
